import { type DefaultValues, useForm as useHookForm } from 'react-hook-form'
import type {
  FieldValues,
  UseFormProps as UseHookFormProps,
} from 'react-hook-form'
import type { z } from 'zod'
import type { Resolver } from '../resolver/types.ts'
import { zodInternalResolver } from '../resolver/zod.ts'

type AsyncDefaultValues<TFieldValues> = (
  payload?: unknown
) => Promise<TFieldValues>

export type UseFormProps<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodSchema,
  TResolvedValues extends FieldValues = TSchema extends undefined
    ? TFieldValues
    : z.infer<TSchema>,
> = Omit<
  UseHookFormProps<TFieldValues, TContext>,
  'defaultValues' | 'resolver' | 'values'
> & {
  defaultValues?: DefaultValues<TFieldValues> | AsyncDefaultValues<TFieldValues>
  resolver?: Resolver<TSchema, TResolvedValues>
  values?: TFieldValues
}

export function useForm<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodSchema,
  TResolvedValues extends FieldValues = TSchema extends undefined
    ? TFieldValues
    : z.infer<TSchema>,
>(props: UseFormProps<TFieldValues, TContext, TSchema, TResolvedValues>) {
  const resolver = props.resolver
    ? zodInternalResolver<TFieldValues, TContext, TSchema, TResolvedValues>(
        props.resolver.schema,
        props.resolver.tFunction,
        props.resolver.schemaOptions,
        props.resolver.resolverOptions
      )
    : undefined

  const hookForm = useHookForm<
    TFieldValues,
    TContext,
    TSchema extends undefined ? TFieldValues : z.infer<TSchema>
  >({
    ...props,
    resolver,
  })

  return hookForm
}
